import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { scoreColor } from "@/components/ScoreMeter";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Radar, ArrowUp, ArrowRight, ArrowDown, Sparkles, Loader2 } from "lucide-react";
import { GridSkeleton } from "@/components/Loaders";
import { toast } from "sonner";

const VEL = {
  rising: { icon: ArrowUp, color: "#10B981", label: "Rising" },
  stable: { icon: ArrowRight, color: "#06B6D4", label: "Stable" },
  declining: { icon: ArrowDown, color: "#EF4444", label: "Declining" },
};

export default function TrendRadar() {
  const { user } = useAuth();
  const [trends, setTrends] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [ideas, setIdeas] = useState(null);
  const [genLoading, setGenLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    const q = filter !== "all" ? `?velocity=${filter}` : "";
    api.get(`/trends${q}`).then((r) => setTrends(r.data)).finally(() => setLoading(false));
  }, [filter]);

  const buildFromTrend = async (trend) => {
    if (!user) { window.location.href = "/login"; return; }
    setActive(trend); setIdeas(null); setGenLoading(true);
    try {
      const res = await api.post(`/ai/trend-build/${encodeURIComponent(trend.name)}`);
      setIdeas(res.data.opportunities || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setGenLoading(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="mb-8">
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-50 flex items-center gap-3"><Radar className="w-8 h-8 text-cyan-400" />Trend Radar</h1>
        <p className="text-slate-400 mt-1">Emerging technologies, industries and keywords — scored by momentum and commercial potential.</p>
      </div>

      <div className="flex gap-2 mb-8">
        {["all", "rising", "stable", "declining"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1.5 rounded-lg text-sm border capitalize ${filter === f ? "border-cyan-500 bg-cyan-500/15 text-cyan-200" : "border-white/10 text-slate-400"}`} data-testid={`trend-filter-${f}`}>{f}</button>
        ))}
      </div>

      {loading ? <GridSkeleton /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {trends.map((t) => {
            const v = VEL[t.velocity] || VEL.stable; const Vi = v.icon;
            return (
              <div key={t.name} className="glass rounded-xl p-5 card-hover" data-testid={`trend-card-${t.name.toLowerCase().replace(/[^a-z]+/g,'-')}`}>
                <div className="flex items-start justify-between mb-2">
                  <span className="text-[11px] font-mono uppercase text-indigo-400 px-2 py-0.5 rounded bg-indigo-500/10 border border-indigo-500/20">{t.category}</span>
                  <span className="flex items-center gap-1 text-xs font-mono" style={{ color: v.color }}><Vi className="w-3.5 h-3.5" />{v.label}</span>
                </div>
                <h3 className="text-lg font-semibold text-slate-100 mb-1">{t.name}</h3>
                <p className="text-sm text-slate-400 mb-4">{t.description}</p>
                <div className="grid grid-cols-3 gap-2 mb-4 text-center">
                  <M label="Trend" v={t.trend_score} /><M label="Commercial" v={t.commercial_potential} /><M label="Competition" v={t.competition} invert />
                </div>
                <div className="text-xs text-slate-500 mb-3"><span className="text-slate-400">Best opportunity: </span>{t.best_opportunity}</div>
                <Button size="sm" onClick={() => buildFromTrend(t)} className="w-full bg-cyan-500/15 border border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/25" data-testid={`trend-build-${t.name.toLowerCase().replace(/[^a-z]+/g,'-')}`}>
                  <Sparkles className="w-3.5 h-3.5 mr-1.5" />What should I build around this?
                </Button>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="glass border-white/10 text-slate-200 max-w-lg">
          <DialogHeader><DialogTitle className="text-slate-50">Build around: {active?.name}</DialogTitle></DialogHeader>
          {genLoading ? <div className="py-10 text-center"><Loader2 className="w-6 h-6 text-cyan-400 animate-spin mx-auto" /></div> : (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto">
              {(ideas || []).map((idea, i) => (
                <div key={i} className="rounded-lg bg-white/5 border border-white/8 p-4">
                  <h4 className="font-semibold text-slate-100">{idea.title}</h4>
                  <p className="text-sm text-slate-400 mt-1">{idea.description}</p>
                  <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-xs text-slate-500"><span>👤 {idea.target_customer}</span><span>💰 {idea.monetization}</span><span>🔧 {idea.difficulty}</span></div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
function M({ label, v = 0, invert }) {
  return <div><p className="font-mono font-bold" style={{ color: scoreColor(invert ? 100 - v : v) }}>{v}</p><p className="text-[9px] uppercase text-slate-600">{label}</p></div>;
}
